import { useRef, useState, useEffect } from 'react';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import Modal from '../../../../elements/Modal';
import Button from '../../../../forms/Button';
import { toast } from 'react-toastify';

const CropperModal = ({
  isOpen,
  setIsOpen,
  imageUrl,
  originalUrl, // Add this prop to pass original image
  cropData, // Add this prop to pass previous crop data
  onCrop,
  aspectRatio = 8 / 11,
}) => {
  const cropperRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [cropperReady, setCropperReady] = useState(false);
  const [imageLoading, setImageLoading] = useState(true); // New state for image loading

  // Reset states when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setCropperReady(false);
      setImageLoading(true); // Reset image loading state
    }
  }, [isOpen]);

  // Restore crop data when cropper is ready
  useEffect(() => {
    if (cropperReady && cropData && cropperRef.current?.cropper) {
      const cropper = cropperRef.current.cropper;
      cropper.setData(cropData);
    }
  }, [cropperReady, cropData]);

  const handleCropperReady = () => {
    setCropperReady(true);
    setImageLoading(false); // Image has loaded and cropper is ready
  };

  // Handle image load error
  const handleImageError = () => {
    setImageLoading(false);
    toast.error('Failed to load image');
  };

  const handleCrop = async () => {
    const cropper = cropperRef.current?.cropper;

    // Validate cropper instance
    if (!cropper) {
      console.error('Cropper instance not found');
      setLoading(false);
      return;
    }

    // Check if cropper is ready
    if (!cropperReady) {
      console.error('Cropper not ready yet');
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      // Validate cropper state
      const imageData = cropper.getImageData();
      if (
        !imageData ||
        imageData.naturalWidth === 0 ||
        imageData.naturalHeight === 0
      ) {
        throw new Error(
          'Invalid image data - image may not have loaded properly'
        );
      }

      // Get current crop data with validation
      const currentCropData = cropper.getData();
      if (
        !currentCropData ||
        currentCropData.width <= 0 ||
        currentCropData.height <= 0
      ) {
        throw new Error('Invalid crop data - crop area may be too small');
      }

      // Get cropped canvas with explicit options
      const canvas = cropper.getCroppedCanvas({
        width: Math.min(800, Math.floor(currentCropData.width)),
        height: Math.min(1100, Math.floor(currentCropData.height)),
        minWidth: 100,
        minHeight: 137.5, // 100 * (11/8) for aspect ratio
        maxWidth: 1600,
        maxHeight: 2200,
        fillColor: '#ffffff',
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
      });

      // Validate canvas creation
      if (!canvas) {
        throw new Error(
          'Failed to generate cropped canvas - image may be corrupted or too large'
        );
      }

      // Validate canvas dimensions
      if (canvas.width === 0 || canvas.height === 0) {
        throw new Error('Generated canvas has invalid dimensions');
      }

      // Convert canvas to blob with error handling
      canvas.toBlob(
        (blob) => {
          try {
            if (!blob) {
              throw new Error('Failed to generate blob from canvas');
            }

            // Validate blob
            if (blob.size === 0) {
              throw new Error('Generated blob is empty');
            }

            if (blob.size > 10 * 1024 * 1024) {
              // 10MB limit
              console.warn('Generated blob is very large:', blob.size);
            }

            // Create object URL with error handling
            let newUrl;
            try {
              newUrl = URL.createObjectURL(blob);
            } catch (urlError) {
              throw new Error(
                `Failed to create object URL: ${urlError.message}`
              );
            }

            // Call onCrop with validated data
            onCrop({
              blob,
              url: newUrl,
              cropData: currentCropData,
            });

            setIsOpen(false);
          } catch (blobError) {
            console.error('Error processing blob:', blobError);
            toast.error(
              `Failed to process cropped image: ${blobError.message}`
            );
          } finally {
            setLoading(false);
          }
        },
        'image/jpeg',
        0.9 // Quality setting
      );
    } catch (error) {
      console.error('Error during crop operation:', error);
      setLoading(false);

      // Show user-friendly error message
      toast.error(`Crop failed: ${error.message}`);

      // Optional: Log additional debug info
      console.log('Debug info:', {
        cropperReady,
        sourceUrl: originalUrl || imageUrl,
        cropperInstance: !!cropper,
        imageData: cropper ? cropper.getImageData() : null,
      });
    }
  };

  // Use original URL if available, otherwise use current URL
  const sourceUrl = originalUrl || imageUrl;

  // Loading spinner component
  const LoadingSpinner = () => (
    <div className="flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-md-pink"></div>
    </div>
  );

  return (
    <Modal title='Crop Image' isOpen={isOpen} setIsOpen={setIsOpen} size='xl3'>
      <div className='space-y-4'>
        {sourceUrl ? (
          <div className="relative">
            {/* Loading overlay */}
            {imageLoading && (
              <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex flex-col items-center justify-center gap-4 z-10 rounded-lg">
                <LoadingSpinner />
                <p className="text-md-dark font-medium">Loading image...</p>
              </div>
            )}
            
            {/* Cropper component */}
            <Cropper
              src={sourceUrl}
              style={{ 
                height: 400, 
                width: '100%',
                opacity: imageLoading ? 0.3 : 1,
                transition: 'opacity 0.3s ease'
              }}
              aspectRatio={aspectRatio}
              guides={true}
              ref={cropperRef}
              viewMode={1}
              dragMode='crop'
              cropBoxResizable
              cropBoxMovable
              autoCrop
              autoCropArea={1}
              responsive={true}
              restore={false}
              checkCrossOrigin
              crossOrigin='anonymous'
              ready={handleCropperReady}
              error={handleImageError}
            />
          </div>
        ) : (
          <div className='flex flex-col items-center justify-center gap-4 min-h-80'>
            <div className='w-32 h-32 border border-md-pink-light-1 rounded-xl overflow-hidden p-8'>
              <svg
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='currentColor'
                className='w-full h-full text-md-pink'
              >
                <g>
                  <path d='M17.453 24c-.168 0-.34-.021-.51-.066L1.48 19.793a2.018 2.018 0 0 1-1.414-2.45l1.951-7.272a.5.5 0 0 1 .966.258l-1.95 7.27c-.139.53.179 1.082.71 1.229L17.2 22.967a.995.995 0 0 0 1.217-.704l.781-2.894a.499.499 0 1 1 .966.26l-.78 2.89A1.997 1.997 0 0 1 17.453 24z' />
                  <path d='M22 18H6c-1.103 0-2-.897-2-2V4c0-1.103.897-2 2-2h16c1.103 0 2 .897 2 2v12c0 1.103-.897 2-2 2zM6 3c-.551 0-1 .449-1 1v12c0 .551.449 1 1 1h16c.551 0 1-.449 1-1V4c0-.551-.449-1-1-1z' />
                  <path d='M9 9c-1.103 0-2-.897-2-2s.897-2 2-2 2 .897 2 2-.897 2-2 2zm0-3c-.551 0-1 .449-1 1s.449 1 1 1 1-.449 1-1-.449-1-1-1zM4.57 16.93a.5.5 0 0 1-.354-.853l4.723-4.723c.566-.566 1.555-.566 2.121 0l1.406 1.406 3.892-4.67a1.502 1.502 0 0 1 1.142-.54h.011a1.5 1.5 0 0 1 1.139.523l5.23 6.102a.5.5 0 1 1-.759.651l-5.23-6.102a.495.495 0 0 0-.38-.174.53.53 0 0 0-.384.18l-4.243 5.091a.498.498 0 0 1-.362.179.481.481 0 0 1-.376-.146l-1.793-1.793a.514.514 0 0 0-.707 0l-4.723 4.723a.498.498 0 0 1-.353.146z' />
                </g>
              </svg>
            </div>
            <div className='text-xl text-md-dark'>No image to crop</div>
          </div>
        )}
        
        <div className='flex justify-end gap-3'>
          <Button
            buttonLabel='Cancel'
            buttonFunction={() => setIsOpen(false)}
          />
          <Button
            buttonLabel={loading ? 'Processing...' : 'Crop & Save'}
            buttonFunction={handleCrop}
            isDisable={loading || imageLoading} // Disable while image is loading too
          />
        </div>
      </div>
    </Modal>
  );
};

export default CropperModal;